import { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { login } from '../services/auth'
import { useToast } from './Toast'

export default function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { showToast } = useToast()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await login({ username, password })
      showToast('Access granted. Welcome back.', 'success')
      navigate('/queue')
    } catch (err: any) {
      const errorMessage = err?.error || 'Access denied. Invalid credentials.'
      setError(errorMessage)
      showToast(errorMessage, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Central radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[radial-gradient(circle,rgba(143,0,255,0.15)_0%,transparent_70%)] animate-[breathe_8s_ease-in-out_infinite]" />
        
        {/* Floating geometric shapes */}
        <div className="absolute top-20 left-[15%] w-20 h-20 border border-[var(--weave-400)] opacity-20 rotate-45 animate-[float_6s_ease-in-out_infinite]" />
        <div className="absolute bottom-32 right-[20%] w-16 h-16 border border-[var(--energy-cyan)] opacity-15 rotate-12 animate-[float_8s_ease-in-out_infinite_0.5s]" />
        <div className="absolute top-[40%] right-[10%] w-12 h-12 border-2 border-[var(--weave-300)] opacity-10 rotate-[30deg] animate-[float_7s_ease-in-out_infinite_1s]" />
      </div>

      {/* Login Panel */}
      <div className="glass-panel w-full max-w-md p-8 relative">
        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[var(--energy-cyan)] opacity-60" />
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[var(--energy-cyan)] opacity-60" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[var(--weave-400)] opacity-40" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[var(--weave-400)] opacity-40" />

        {/* Header */}
        <div className="text-center mb-8">
          {/* Logo */}
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-2 border-[var(--energy-cyan)] rotate-45 animate-[pulse-glow_3s_ease-in-out_infinite]" />
            <div className="absolute inset-3 border border-[var(--weave-400)] rotate-45 opacity-60" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-bold text-[var(--energy-cyan)]">I</span>
            </div>
          </div>
          
          <h1 className="text-2xl font-bold tracking-wider mb-2">
            ACCESS PORTAL
          </h1>
          <p className="text-sm text-[var(--chrome-300)] tracking-wide">
            Enter credentials to access the Ideaverse
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-4 border border-[var(--alert-coral)] bg-[rgba(255,64,64,0.1)] rounded">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[var(--alert-coral)] rounded-full animate-pulse" />
                <span className="text-sm text-[var(--alert-coral)]">{error}</span>
              </div>
            </div>
          )}

          {/* Username Field */}
          <div className="space-y-2">
            <label htmlFor="username" className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[var(--energy-cyan)] rotate-45" />
              <span>Username</span>
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
              placeholder="Enter username"
              className="w-full"
              autoComplete="username"
            />
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <label htmlFor="password" className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[var(--energy-cyan)] rotate-45" />
              <span>Password</span>
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              placeholder="Enter password"
              className="w-full"
              autoComplete="current-password"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 relative overflow-hidden group"
          >
            {/* Shimmer effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            
            {isLoading ? (
              <span className="flex items-center justify-center gap-3">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Authenticating...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span>Initialize Connection</span>
                <span className="text-xs opacity-70">→</span>
              </span>
            )}
          </button>
        </form>

        {/* Footer accent */}
        <div className="mt-8 flex justify-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-[var(--glass-border)]" />
            <div className="w-1 h-1 bg-[var(--weave-400)] rotate-45" />
            <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-[var(--glass-border)]" />
          </div>
        </div>
      </div>
    </div>
  )
}
